import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Radio, Plus, Trash2, Save, RefreshCw, AlertTriangle, Check, X, Music } from "lucide-react";
import { toast } from "sonner";
import { API, AuthContext } from "../App";

const GENRE_INFO = {
  rock: { icon: "🎸", title: "Rock Radio" },
  metal: { icon: "🤘", title: "All Metal Radio" },
  country: { icon: "🤠", title: "Country Radio" },
  dance: { icon: "💃", title: "Dance Radio" },
  mexican: { icon: "🇲🇽", title: "Mexican Radio" },
  ukraine: { icon: "🇺🇦", title: "Ukraine Radio" },
  comedy: { icon: "🎭", title: "Comedy Radio" },
  middleeast: { icon: "🕌", title: "Middle East Radio" }
};

const AdminRadioManager = () => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allStations, setAllStations] = useState({});
  const [selectedGenre, setSelectedGenre] = useState("rock");
  const [editingStations, setEditingStations] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [testingUrl, setTestingUrl] = useState(null);
  const [testResult, setTestResult] = useState({});

  useEffect(() => {
    fetchAllStations();
  }, []);

  useEffect(() => {
    if (allStations[selectedGenre]) {
      setEditingStations([...allStations[selectedGenre].stations]);
      setHasChanges(false);
    }
  }, [selectedGenre, allStations]);

  const fetchAllStations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/radio/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllStations(response.data);
    } catch (error) {
      console.error("Failed to fetch radio stations:", error);
      toast.error("Failed to load radio stations");
    } finally {
      setLoading(false);
    }
  };

  const handleStationChange = (index, field, value) => {
    const updated = [...editingStations];
    updated[index] = { ...updated[index], [field]: value };
    setEditingStations(updated);
    setHasChanges(true);
  };

  const handleAddStation = () => {
    setEditingStations([...editingStations, { name: "", url: "", genre: "" }]);
    setHasChanges(true);
  };

  const handleRemoveStation = (index) => {
    const updated = editingStations.filter((_, i) => i !== index);
    setEditingStations(updated);
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Validate all stations have name and url
    const validStations = editingStations.filter(s => s.name?.trim() && s.url?.trim());
    if (validStations.length === 0) {
      toast.error("Add at least one station with name and URL");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/radio/stations/${selectedGenre}`, 
        validStations,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Saved ${validStations.length} stations for ${GENRE_INFO[selectedGenre]?.title || selectedGenre}`);
      setHasChanges(false);
      fetchAllStations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save stations");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(`Reset ${GENRE_INFO[selectedGenre]?.title || selectedGenre} to default stations? This cannot be undone.`)) {
      return;
    }

    setSaving(true);
    try {
      await axios.delete(`${API}/admin/radio/stations/${selectedGenre}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Reset to default stations");
      setHasChanges(false);
      fetchAllStations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset stations");
    } finally {
      setSaving(false);
    }
  };

  const testStation = async (url, index) => {
    setTestingUrl(url);
    setTestResult(prev => ({ ...prev, [index]: 'testing' }));
    
    try {
      // Create a test audio element
      const audio = new Audio();
      audio.volume = 0;
      
      const timeout = setTimeout(() => {
        audio.pause();
        audio.src = '';
        setTestResult(prev => ({ ...prev, [index]: 'timeout' }));
        setTestingUrl(null);
      }, 10000);

      audio.oncanplay = () => {
        clearTimeout(timeout);
        audio.pause();
        audio.src = '';
        setTestResult(prev => ({ ...prev, [index]: 'success' }));
        setTestingUrl(null);
      };

      audio.onerror = () => {
        clearTimeout(timeout);
        setTestResult(prev => ({ ...prev, [index]: 'error' }));
        setTestingUrl(null);
      };

      audio.src = url;
      audio.load();
    } catch (err) {
      setTestResult(prev => ({ ...prev, [index]: 'error' }));
      setTestingUrl(null);
    }
  };

  const getTestIcon = (index) => {
    const result = testResult[index];
    if (result === 'testing') return <RefreshCw className="w-3 h-3 animate-spin text-blue-500" />;
    if (result === 'success') return <Check className="w-3 h-3 text-green-500" />;
    if (result === 'error' || result === 'timeout') return <X className="w-3 h-3 text-red-500" />;
    return null;
  };

  if (loading) {
    return (
      <div className="p-4 text-center">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
        <p className="text-sm text-gray-600">Loading radio stations...</p>
      </div>
    );
  }

  return (
    <div className="p-3" data-testid="admin-radio-manager">
      <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
        <Radio className="w-5 h-5 text-[#7B0099]" /> 
        Radio Station Manager
      </h3>

      {/* Genre Selector */}
      <div className="flex flex-wrap gap-1 mb-3">
        {Object.entries(GENRE_INFO).map(([genre, info]) => (
          <button
            key={genre}
            className={`retro-button text-xs px-2 py-1 flex items-center gap-1 ${
              selectedGenre === genre ? 'bg-[#7B0099] text-white' : ''
            }`}
            onClick={() => setSelectedGenre(genre)}
          >
            <span>{info.icon}</span>
            <span>{info.title}</span>
            {allStations[genre]?.is_custom && <span className="text-yellow-400">*</span>}
          </button>
        ))}
      </div>

      {/* Status bar */}
      <div className="flex items-center gap-2 mb-2 text-xs">
        <span className={allStations[selectedGenre]?.is_custom ? 'text-yellow-600 font-bold' : 'text-green-600'}>
          {allStations[selectedGenre]?.is_custom ? '⚙️ Custom Configuration' : '📋 Using Defaults'}
        </span>
        {hasChanges && (
          <span className="text-orange-600 font-bold flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> Unsaved changes
          </span>
        )}
      </div>

      {/* Station Editor */}
      <div className="retro-inset p-2 mb-3 max-h-64 overflow-y-auto retro-scrollbar">
        {editingStations.length === 0 ? (
          <p className="text-center text-gray-500 text-sm py-4">No stations configured</p>
        ) : (
          <div className="space-y-2">
            {editingStations.map((station, index) => (
              <div key={index} className="retro-window p-2 bg-gray-50">
                <div className="flex items-start gap-2">
                  <div className="flex-1 space-y-1">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={station.name || ''}
                        onChange={(e) => handleStationChange(index, 'name', e.target.value)}
                        className="retro-input text-xs flex-1"
                        placeholder="Station Name"
                      />
                      <input
                        type="text"
                        value={station.genre || ''}
                        onChange={(e) => handleStationChange(index, 'genre', e.target.value)}
                        className="retro-input text-xs w-24"
                        placeholder="Sub-genre"
                      />
                    </div>
                    <div className="flex gap-1">
                      <input
                        type="text"
                        value={station.url || ''}
                        onChange={(e) => handleStationChange(index, 'url', e.target.value)}
                        className="retro-input text-xs flex-1 font-mono"
                        placeholder="Stream URL (https://...)"
                      />
                      <button
                        className="retro-button text-[10px] px-2"
                        onClick={() => testStation(station.url, index)}
                        disabled={!station.url || testingUrl === station.url}
                        title="Test stream"
                      >
                        {getTestIcon(index) || <Music className="w-3 h-3" />}
                      </button>
                    </div>
                  </div>
                  <button
                    className="retro-button p-1 text-red-600 hover:bg-red-100"
                    onClick={() => handleRemoveStation(index)}
                    title="Remove station"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          className="retro-button text-xs px-3 py-1 flex items-center gap-1"
          onClick={handleAddStation}
        >
          <Plus className="w-3 h-3" /> Add Station
        </button>
        
        <button
          className="retro-button text-xs px-3 py-1 flex items-center gap-1 bg-green-100"
          onClick={handleSave}
          disabled={saving || !hasChanges}
        >
          <Save className="w-3 h-3" /> {saving ? 'Saving...' : 'Save Changes'}
        </button>
        
        {allStations[selectedGenre]?.is_custom && (
          <button
            className="retro-button text-xs px-3 py-1 flex items-center gap-1 bg-yellow-100"
            onClick={handleReset}
            disabled={saving}
          >
            <RefreshCw className="w-3 h-3" /> Reset to Defaults
          </button>
        )}
        
        <button
          className="retro-button text-xs px-3 py-1 flex items-center gap-1 ml-auto"
          onClick={fetchAllStations}
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* Help text */}
      <p className="text-[10px] text-gray-500 mt-2">
        💡 Tip: Test station URLs before saving. Changes take effect immediately for all users in music rooms.
      </p>
    </div>
  );
};

export default AdminRadioManager;
